import React, { useState } from 'react';
import { useAuth, useBrand } from '../App';
import { 
  Sparkles, Upload, Palette, Type, Image, FileText, Plus,
  Download, Trash2, Copy, Check, X, Edit2, Save, RotateCcw
} from 'lucide-react';
import { toast } from 'sonner';

const defaultFonts = [
  { name: 'Heading', family: 'Inter', weight: '700', preview: 'The quick brown fox' },
  { name: 'Body', family: 'Inter', weight: '400', preview: 'The quick brown fox jumps over the lazy dog' },
  { name: 'Accent', family: 'Inter', weight: '500', preview: 'Call to Action' },
];

const initialAssets = [
  { id: '1', name: 'Company Logo', type: 'logo', url: '/logo.png', format: 'PNG', size: '24 KB' },
  { id: '2', name: 'Logo Dark', type: 'logo', url: '/logo-dark.png', format: 'PNG', size: '22 KB' },
  { id: '3', name: 'Icon', type: 'icon', url: '/icon.png', format: 'PNG', size: '8 KB' },
  { id: '4', name: 'Brand Pattern', type: 'pattern', url: '/pattern.png', format: 'PNG', size: '156 KB' },
];

export default function BrandManagement() {
  const { token } = useAuth();
  const { primaryColor, updatePrimaryColor, resetPrimaryColor, DEFAULT_PRIMARY } = useBrand();
  const [activeTab, setActiveTab] = useState('colors');
  const [localPrimaryColor, setLocalPrimaryColor] = useState(primaryColor);
  const [fonts, setFonts] = useState(defaultFonts);
  const [assets, setAssets] = useState(initialAssets);
  const [copiedColor, setCopiedColor] = useState(false);
  const [brandGuidelines, setBrandGuidelines] = useState({
    voiceTone: 'Professional, friendly, and innovative',
    tagline: 'Empowering your marketing with AI',
    mission: 'To help businesses grow through intelligent automation',
    keywords: ['Innovation', 'Growth', 'AI-Powered', 'Efficient', 'Reliable']
  });

  const copyToClipboard = () => {
    navigator.clipboard.writeText(localPrimaryColor);
    setCopiedColor(true);
    toast.success('Primary color copied to clipboard');
    setTimeout(() => setCopiedColor(false), 2000);
  };

  const handleColorChange = (value) => {
    setLocalPrimaryColor(value);
    // Update in real-time
    updatePrimaryColor(value);
  };

  const handleApplyColor = () => {
    updatePrimaryColor(localPrimaryColor);
    toast.success('Primary color applied across dashboard!');
  };

  const handleResetColor = () => {
    setLocalPrimaryColor(DEFAULT_PRIMARY);
    resetPrimaryColor();
    toast.success('Primary color reset to default');
  };

  const handleFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      const newAsset = {
        id: Date.now().toString(),
        name: file.name.split('.')[0],
        type: 'custom',
        url: URL.createObjectURL(file),
        format: file.name.split('.').pop()?.toUpperCase() || 'FILE',
        size: `${(file.size / 1024).toFixed(1)} KB`
      };
      setAssets([...assets, newAsset]);
      toast.success('Asset uploaded successfully');
    }
  };

  const deleteAsset = (id) => {
    setAssets(assets.filter(a => a.id !== id));
    toast.success('Asset deleted');
  };

  const tabs = [
    { id: 'colors', label: 'Primary Color', icon: Palette },
    { id: 'typography', label: 'Typography', icon: Type },
    { id: 'assets', label: 'Assets', icon: Image },
    { id: 'guidelines', label: 'Guidelines', icon: FileText },
  ];

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Sparkles className="w-6 h-6 text-primary" />
            Brand Management
          </h1>
          <p className="text-muted-foreground">Customize your primary color - changes apply in real-time</p>
        </div>
        <div className="flex items-center gap-2">
          <button 
            onClick={handleResetColor}
            className="marketing-btn-secondary flex items-center gap-2" 
            data-testid="reset-brand-btn"
          >
            <RotateCcw className="w-4 h-4" />
            <span>Reset</span>
          </button>
          <button 
            onClick={handleApplyColor}
            className="marketing-btn-primary flex items-center gap-2" 
            data-testid="save-brand-btn"
          >
            <Save className="w-4 h-4" />
            <span>Apply Color</span>
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-border pb-2 overflow-x-auto">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors whitespace-nowrap ${
              activeTab === tab.id 
                ? 'bg-primary text-primary-foreground' 
                : 'hover:bg-secondary'
            }`}
            data-testid={`tab-${tab.id}`}
          >
            <tab.icon className="w-4 h-4" />
            <span>{tab.label}</span>
          </button>
        ))}
      </div>

      {/* Primary Color Tab */}
      {activeTab === 'colors' && (
        <div className="space-y-6">
          {/* Primary Color Card */}
          <div className="marketing-card">
            <h3 className="font-semibold mb-4">Primary Color</h3>
            <p className="text-sm text-muted-foreground mb-6">
              Choose your brand's primary color. This will be used for buttons, highlights, and accent elements throughout the dashboard.
            </p>
            
            <div className="flex flex-col md:flex-row gap-6">
              {/* Color Picker */}
              <div className="flex-1">
                <div className="bg-secondary/50 rounded-xl p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <p className="font-medium">Primary Color</p>
                      <p className="text-xs text-muted-foreground">Used for buttons, links, highlights</p>
                    </div>
                    <button 
                      onClick={copyToClipboard}
                      className="p-2 hover:bg-secondary rounded-lg transition-colors"
                    >
                      {copiedColor ? (
                        <Check className="w-4 h-4 text-emerald-400" />
                      ) : (
                        <Copy className="w-4 h-4 text-muted-foreground" />
                      )}
                    </button>
                  </div>
                  
                  <div className="flex items-center gap-4">
                    <div 
                      className="w-20 h-20 rounded-xl border-2 border-border shadow-lg"
                      style={{ backgroundColor: localPrimaryColor }}
                    ></div>
                    <div className="flex-1 space-y-3">
                      <input
                        type="text"
                        value={localPrimaryColor}
                        onChange={(e) => handleColorChange(e.target.value)}
                        className="marketing-input w-full font-mono text-lg"
                        placeholder="#E36559"
                        data-testid="primary-color-input"
                      />
                      <input
                        type="color"
                        value={localPrimaryColor}
                        onChange={(e) => handleColorChange(e.target.value)}
                        className="w-full h-10 rounded-lg cursor-pointer"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Live Preview */}
              <div className="flex-1">
                <div className="bg-secondary/50 rounded-xl p-6">
                  <h4 className="font-medium mb-4">Live Preview</h4>
                  
                  {/* Button Preview */}
                  <div className="space-y-4">
                    <div>
                      <p className="text-xs text-muted-foreground mb-2">Buttons</p>
                      <div className="flex flex-wrap gap-2">
                        <button 
                          className="px-4 py-2 rounded-lg font-medium text-white transition-all"
                          style={{ backgroundColor: localPrimaryColor }}
                        >
                          Primary Button
                        </button>
                        <button 
                          className="px-4 py-2 rounded-lg font-medium border-2 transition-all"
                          style={{ 
                            borderColor: localPrimaryColor,
                            color: localPrimaryColor 
                          }}
                        >
                          Outline Button
                        </button>
                      </div>
                    </div>

                    {/* Badge Preview */}
                    <div>
                      <p className="text-xs text-muted-foreground mb-2">Badges & Tags</p>
                      <div className="flex flex-wrap gap-2">
                        <span 
                          className="px-3 py-1 rounded-full text-sm font-medium text-white"
                          style={{ backgroundColor: localPrimaryColor }}
                        >
                          Active
                        </span>
                        <span 
                          className="px-3 py-1 rounded-full text-sm font-medium"
                          style={{ 
                            backgroundColor: `${localPrimaryColor}20`,
                            color: localPrimaryColor 
                          }}
                        >
                          Pending
                        </span>
                      </div>
                    </div>

                    {/* Progress Bar Preview */}
                    <div>
                      <p className="text-xs text-muted-foreground mb-2">Progress Bar</p>
                      <div className="h-3 bg-background rounded-full overflow-hidden">
                        <div 
                          className="h-full rounded-full transition-all"
                          style={{ 
                            backgroundColor: localPrimaryColor,
                            width: '65%'
                          }}
                        ></div>
                      </div>
                    </div>

                    {/* Link Preview */}
                    <div>
                      <p className="text-xs text-muted-foreground mb-2">Links & Text</p>
                      <p className="text-sm">
                        This is regular text with a{' '}
                        <span style={{ color: localPrimaryColor }} className="font-medium cursor-pointer hover:underline">
                          primary color link
                        </span>
                        {' '}inside it.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Info Card */}
          <div className="marketing-card bg-primary/5 border-primary/20">
            <div className="flex items-start gap-3">
              <Sparkles className="w-5 h-5 text-primary mt-0.5" />
              <div>
                <p className="font-medium">How it works</p>
                <p className="text-sm text-muted-foreground mt-1">
                  The primary color you choose will be applied across all buttons, links, progress bars, 
                  and highlight elements in your dashboard. This change applies only to your current session.
                  The base theme (white/dark backgrounds) remains consistent in both light and dark modes.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'typography' && (
        <div className="space-y-6">
          <div className="marketing-card">
            <h3 className="font-semibold mb-4">Font Families</h3>
            <div className="space-y-4">
              {fonts.map((font, index) => (
                <div key={index} className="bg-secondary/50 rounded-xl p-4" data-testid={`font-${index}`}>
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <p className="font-medium">{font.name} Font</p>
                      <p className="text-xs text-muted-foreground">{font.family} - {font.weight}</p>
                    </div>
                    <button className="p-2 hover:bg-secondary rounded-lg transition-colors">
                      <Edit2 className="w-4 h-4 text-muted-foreground" />
                    </button>
                  </div>
                  <div 
                    className="p-4 bg-background rounded-lg"
                    style={{ fontFamily: font.family, fontWeight: font.weight }}
                  >
                    <p className={font.name === 'Heading' ? 'text-2xl' : 'text-base'}>
                      {font.preview}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Typography Scale */}
          <div className="marketing-card">
            <h3 className="font-semibold mb-4">Type Scale</h3>
            <div className="space-y-4">
              {[
                { label: 'Display', size: '48px', class: 'text-5xl' },
                { label: 'H1', size: '36px', class: 'text-4xl' },
                { label: 'H2', size: '30px', class: 'text-3xl' },
                { label: 'H3', size: '24px', class: 'text-2xl' },
                { label: 'Body', size: '16px', class: 'text-base' },
                { label: 'Small', size: '14px', class: 'text-sm' },
              ].map((item, index) => (
                <div key={index} className="flex items-center gap-4 p-3 bg-secondary/30 rounded-lg">
                  <span className="w-20 text-xs text-muted-foreground">{item.label}</span>
                  <span className="w-16 text-xs text-muted-foreground">{item.size}</span>
                  <span className={item.class}>Sample Text</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {activeTab === 'assets' && (
        <div className="space-y-6">
          {/* Upload Section */}
          <div className="marketing-card">
            <div className="border-2 border-dashed border-border rounded-xl p-8 text-center">
              <Upload className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <p className="font-medium mb-2">Upload Brand Assets</p>
              <p className="text-sm text-muted-foreground mb-4">
                Drag & drop files here or click to browse
              </p>
              <label className="marketing-btn-primary inline-flex items-center gap-2 cursor-pointer">
                <Plus className="w-4 h-4" />
                <span>Choose Files</span>
                <input 
                  type="file" 
                  className="hidden" 
                  accept="image/*"
                  onChange={handleFileUpload}
                  data-testid="asset-upload"
                />
              </label>
            </div>
          </div>

          {/* Assets Grid */}
          <div className="marketing-card">
            <h3 className="font-semibold mb-4">Brand Assets</h3>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
              {assets.map(asset => (
                <div key={asset.id} className="bg-secondary/50 rounded-xl p-4" data-testid={`asset-${asset.id}`}>
                  <div className="aspect-square bg-background rounded-lg mb-3 flex items-center justify-center overflow-hidden">
                    <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-primary/20 to-primary/5">
                      <Image className="w-12 h-12 text-primary/50" />
                    </div>
                  </div>
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="font-medium text-sm truncate">{asset.name}</p>
                      <p className="text-xs text-muted-foreground">{asset.format} • {asset.size}</p>
                    </div>
                    <div className="flex gap-1">
                      <button className="p-1.5 hover:bg-secondary rounded-lg transition-colors">
                        <Download className="w-4 h-4 text-muted-foreground" />
                      </button>
                      <button 
                        onClick={() => deleteAsset(asset.id)}
                        className="p-1.5 hover:bg-red-500/20 rounded-lg transition-colors"
                      >
                        <Trash2 className="w-4 h-4 text-red-400" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {activeTab === 'guidelines' && (
        <div className="space-y-6">
          <div className="marketing-card">
            <h3 className="font-semibold mb-4">Brand Voice & Tone</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Voice & Tone</label>
                <textarea
                  value={brandGuidelines.voiceTone}
                  onChange={(e) => setBrandGuidelines({...brandGuidelines, voiceTone: e.target.value})}
                  className="marketing-input w-full h-24 resize-none"
                  placeholder="Describe your brand's voice..."
                  data-testid="voice-tone-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Tagline</label>
                <input
                  type="text"
                  value={brandGuidelines.tagline}
                  onChange={(e) => setBrandGuidelines({...brandGuidelines, tagline: e.target.value})}
                  className="marketing-input w-full"
                  placeholder="Your brand tagline"
                  data-testid="tagline-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Mission Statement</label>
                <textarea
                  value={brandGuidelines.mission}
                  onChange={(e) => setBrandGuidelines({...brandGuidelines, mission: e.target.value})}
                  className="marketing-input w-full h-24 resize-none"
                  placeholder="Your brand's mission..."
                  data-testid="mission-input"
                />
              </div>
            </div>
          </div>

          <div className="marketing-card">
            <h3 className="font-semibold mb-4">Brand Keywords</h3>
            <div className="flex flex-wrap gap-2 mb-4">
              {brandGuidelines.keywords.map((keyword, index) => (
                <span 
                  key={index}
                  className="px-3 py-1.5 bg-primary/20 text-primary rounded-full text-sm flex items-center gap-2"
                >
                  {keyword}
                  <button 
                    onClick={() => setBrandGuidelines({
                      ...brandGuidelines,
                      keywords: brandGuidelines.keywords.filter((_, i) => i !== index)
                    })}
                    className="hover:text-primary/70"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ))}
            </div>
            <div className="flex gap-2">
              <input
                type="text"
                placeholder="Add keyword..."
                className="marketing-input flex-1"
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && e.target.value.trim()) {
                    setBrandGuidelines({
                      ...brandGuidelines,
                      keywords: [...brandGuidelines.keywords, e.target.value.trim()]
                    });
                    e.target.value = '';
                  }
                }}
                data-testid="keyword-input"
              />
              <button className="marketing-btn-secondary">
                <Plus className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
